"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KnowledgeWebSocketHelper = void 0;
const backendImport_1 = require("@front/backendImport");
/**
 * Static helper class for WebSocket-related knowledge operations
 */
class KnowledgeWebSocketHelper {
    /**
     * Check if knowledge needs RAG processing
     */
    static needsRAGProcessing(ragStatus) {
        return ragStatus !== backendImport_1.KnowledgeRAGStatus.COMPLETED &&
            ragStatus !== backendImport_1.KnowledgeRAGStatus.NOT_INDEXED;
    }
    /**
     * Setup knowledge-specific WebSocket listener
     */
    static setupKnowledgeListener(wsService, knowledgeId, onSuccess, onError, notifyService, onProgress) {
        const key = knowledgeId.toString();
        // Prevent duplicate listeners for the same knowledge ID
        if (this.activeListeners.has(key)) {
            this.cleanupKnowledgeListener(key);
        }
        if (!wsService.isActive()) {
            console.error(`[WS] Cannot set up listener for knowledge ${knowledgeId} - websocket service is not active!`);
            return;
        }
        const methodName = `rag_processing_update`;
        const unsubscribe = wsService.listenForMessage('status_update', (response) => {
            var _a, _b, _c;
            if (response.method !== methodName) {
                return;
            }
            const status = (_a = response.data) === null || _a === void 0 ? void 0 : _a.status;
            const responseKnowledgeId = (_b = response.data) === null || _b === void 0 ? void 0 : _b.knowledgeId;
            // For temporary listeners (using timestamp as ID), accept any knowledge.added status
            // For real knowledge ID listeners, match the exact ID
            const isTemporaryListener = /^\d{13}$/.test(key); // Timestamp format
            const shouldProcess = isTemporaryListener ?
                (status === 'knowledge.added' || status === 'knowledge.error' || status === 'knowledge.progress') :
                (responseKnowledgeId && responseKnowledgeId.toString() === knowledgeId.toString());
            if (!shouldProcess) {
                return;
            }
            if (status === 'knowledge.progress' && ((_c = response.data) === null || _c === void 0 ? void 0 : _c.percentage) !== undefined) {
                if (onProgress) {
                    onProgress({ images: response.data.images || false, percentage: response.data.percentage });
                }
                return;
            }
            if (status === 'knowledge.added') {
                // Clean up this listener BEFORE calling success to prevent race conditions
                this.cleanupKnowledgeListener(key);
                onSuccess(responseKnowledgeId || knowledgeId);
            }
            else if (status === 'knowledge.error') {
                if (notifyService) {
                    notifyService.notify('rag.file.indexing_error'.t(), 'error');
                }
                // Clean up this listener BEFORE calling error to prevent race conditions
                this.cleanupKnowledgeListener(key);
                onError(responseKnowledgeId || knowledgeId);
            }
        });
        // Store the unsubscribe function
        this.activeListeners.set(key, unsubscribe);
    }
    /**
     * Clean up knowledge-specific listener for a single knowledge ID
     */
    static cleanupKnowledgeListener(knowledgeId) {
        const unsubscribe = this.activeListeners.get(knowledgeId);
        if (unsubscribe) {
            try {
                unsubscribe();
            }
            catch (error) {
                console.error(`[WS] Error unsubscribing listener for knowledge ${knowledgeId}:`, error);
            }
            this.activeListeners.delete(knowledgeId);
        }
    }
    /**
     * Clean up all knowledge-specific listeners
     */
    static cleanupAllListeners() {
        const listenerIds = Array.from(this.activeListeners.keys());
        for (const knowledgeId of listenerIds) {
            const unsubscribe = this.activeListeners.get(knowledgeId);
            if (unsubscribe) {
                try {
                    unsubscribe();
                }
                catch (error) {
                    console.error(`[WS] Error cleaning up listener ${knowledgeId}:`, error);
                }
            }
        }
        this.activeListeners.clear();
    }
    /**
     * Get count of active listeners (for debugging)
     */
    static getActiveListenerCount() {
        return this.activeListeners.size;
    }
    /**
     * Get list of active listener knowledge IDs (for debugging)
     */
    static getActiveListenerIds() {
        return Array.from(this.activeListeners.keys());
    }
}
exports.KnowledgeWebSocketHelper = KnowledgeWebSocketHelper;
KnowledgeWebSocketHelper.activeListeners = new Map();
